import { useEffect, useState } from 'react';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { canViewContact, watchPath } from '../../services/crmService';
import { watchAccessible } from '../../services/recordAccess';
import { visibleLeadsFor, ownedByOf } from '../../shared/leadPermissions';
import { normalizeRole } from '../../config/roles';

// Contacts the signed-in user may see: their own plus ones shared with them (admins see all).
// Non-admins load from their list index (crm/access/{uid}/contacts); the database blocks the rest.
// Archived contacts are left out (dialer, campaigns, lists, email) unless includeArchived is set (Contacts page).
// leadsOnly (default): agents see only leads assigned to them or entered by them (Kelly, Nov 3).
// Pass leadsOnly: false for deal-linked lookups (person peek) so shared deal contacts still resolve.
export const useVisibleContacts = ({ includeArchived = false, leadsOnly = true } = {}) => {
  const { user, userRole } = useAuth();
  const isAdmin = ['Admin', 'Marketing Manager'].includes(userRole); // view-all roles
  const [all, setAll] = useState([]);
  useEffect(() => watchAccessible('contacts', user?.uid, isAdmin, setAll), [user?.uid, isAdmin]);
  const seen = all.filter((c) => canViewContact(c, user?.uid, isAdmin) && (includeArchived || !c.archived));
  return leadsOnly ? visibleLeadsFor(seen, user?.uid, normalizeRole(userRole)) : seen;
};

// Everyone on the team (for Lead Owner / Assigned To / Transfer / Share pickers).
export const useTeam = () => {
  const [team, setTeam] = useState([]);
  useEffect(() => onValue(ref(database, 'users'), (snap) => {
    const list = Object.entries(snap.val() || {})
      // Everyone with a user record, admins and team leaders included; name falls back to the profile name or email.
      .filter(([, u]) => u && typeof u === 'object')
      .map(([uid, u]) => ({ uid, name: u.displayName || [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email || u.profile?.contactEmail || 'Team member', role: u.role || '', email: u.email || u.profile?.contactEmail || '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setTeam(list);
  }, () => setTeam([])), []);
  return team;
};

// Admins and the Marketing Manager see every CRM record (view/market); only admins share, transfer or delete.
export const VIEW_ALL_ROLES = ['Admin', 'Marketing Manager'];
export const canViewAll = (role) => VIEW_ALL_ROLES.includes(role);
// "Owned by Team" = marked team-owned, or owned by an admin (older team leads were owned by the admin). Everything else is "Agent".
export const ownedByTeam = (rec, team, ownerUid) => !!rec && (ownedByOf(rec) === 'team' || rec.ownerType === 'team' || (team || []).some((t) => t.uid === ownerUid && t.role === 'Admin'));
// Shared select for the Team/Agent filter. value: '' | 'team' | 'agent'.
export const ownedFilterMatch = (value, rec, team, ownerUid) => !value || (value === 'team') === ownedByTeam(rec, team, ownerUid);

// Live list of an owned CRM collection (lists, campaigns, enrollments, emailBlasts, templates,
// textTemplates, scripts). Admin / Marketing Manager: everything. Everyone else: their own records
// (createdBy), plus, when scoped, team (master) and shared items. Matches database.rules.json.
export const useOwned = (path, { scoped = false } = {}) => {
  const { user, userRole } = useAuth();
  const uid = user?.uid; const all = canViewAll(userRole);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (all) return watchPath(path, setRows);
    if (!uid) { setRows([]); return undefined; }
    const parts = {};
    const emit = () => { const m = new Map(); Object.values(parts).forEach((l) => l.forEach((r) => m.set(r.id, r))); setRows([...m.values()]); };
    const scopes = Array.isArray(scoped) ? scoped : scoped ? ['team', 'shared'] : [];
    const qs = [['createdBy', uid], ...scopes.map((v) => ['scope', v])];
    const offs = qs.map(([f, v]) => onValue(query(ref(database, path), orderByChild(f), equalTo(v)), (s) => {
      parts[`${f}:${v}`] = Object.entries(s.val() || {}).map(([id, r]) => ({ id, ...r })); emit();
    }, () => { parts[`${f}:${v}`] = []; emit(); }));
    return () => offs.forEach((o) => o());
  }, [path, uid, all, String(scoped)]); // eslint-disable-line react-hooks/exhaustive-deps
  return rows;
};
